import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { useScenarioStore } from '../store/useScenarioStore';
import { AccentCard, Eyebrow, DisclosureRow } from '../components/design';

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: 'var(--tco-bg)',
  fontFamily: 'var(--tco-font-sans)',
  color: 'var(--tco-ink)',
};

const containerStyle: React.CSSProperties = {
  maxWidth: 880,
  margin: '0 auto',
  padding: '40px 32px 80px',
};

const headingStyle: React.CSSProperties = {
  fontSize: 32,
  fontWeight: 600,
  letterSpacing: '-0.015em',
  margin: '8px 0 12px',
  color: 'var(--tco-ink)',
};

const introStyle: React.CSSProperties = {
  fontSize: 15,
  lineHeight: 1.6,
  color: 'var(--tco-ink-2)',
  marginBottom: 32,
  maxWidth: 720,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  margin: '0 0 12px',
  color: 'var(--tco-ink)',
  letterSpacing: '-0.005em',
};

const bodyStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.6,
  color: 'var(--tco-ink-2)',
};

const citationStyle: React.CSSProperties = {
  fontSize: 12,
  lineHeight: 1.55,
  color: 'var(--tco-ink-3)',
  marginTop: 12,
  paddingTop: 12,
  borderTop: '1px solid var(--tco-line-soft)',
};

const confidenceBadgeStyle = (
  tier: 'high' | 'medium' | 'low' | 'na'
): React.CSSProperties => {
  const palette: Record<string, { bg: string; fg: string }> = {
    high: { bg: 'var(--tco-accent-soft)', fg: 'var(--tco-accent)' },
    medium: { bg: 'var(--tco-line-soft)', fg: 'var(--tco-ink-2)' },
    low: { bg: 'var(--tco-neg-soft)', fg: 'var(--tco-neg)' },
    na: { bg: 'var(--tco-line-soft)', fg: 'var(--tco-ink-3)' },
  };
  const p = palette[tier];
  return {
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    padding: '2px 8px',
    borderRadius: 4,
    background: p.bg,
    color: p.fg,
  };
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
  marginTop: 8,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  background: 'var(--tco-line-soft)',
  borderBottom: '1px solid var(--tco-line)',
  color: 'var(--tco-ink-3)',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--tco-line-soft)',
  color: 'var(--tco-ink-2)',
  fontVariantNumeric: 'tabular-nums',
};

const sectionGap = 20;

const VOCATION_DERATE_TABLE: { vocation: string; values: (number | null)[] }[] = [
  { vocation: 'long_haul', values: [0.98, 0.98, 0.98, 0.98, 0.98] },
  { vocation: 'regional_haul', values: [0.736, 0.736, 0.736, 0.736, 0.736] },
  { vocation: 'construction', values: [0.703, 0.703, 0.683, 0.683, 0.45] },
  { vocation: 'vocational_heavy', values: [0.45, 0.45, 0.574, 0.698, 0.681] },
  { vocation: 'local_delivery', values: [0.45, 0.45, 0.45, 0.45, 0.45] },
  { vocation: 'urban_transit', values: [0.45, 0.45, 0.45, 0.45, 0.45] },
  { vocation: 'passenger_transport', values: [0.45, 0.45, 0.45, 0.45, 0.45] },
];

const FRESHNESS_ROWS: { data: string; source: string; version: string; updated: string }[] = [
  { data: 'Passenger MPG', source: 'EPA fueleconomy.gov', version: 'v1.24.0', updated: '2026-04-27' },
  { data: 'Commercial MPG', source: 'Manufacturer specs', version: 'v1.35.0', updated: '2026-05-07' },
  { data: 'MSRP (light-duty)', source: 'KBB / Edmunds / Manufacturer', version: 'v1.19.0', updated: '2026-03' },
  { data: 'MSRP (commercial)', source: 'Dealer / Manufacturer', version: 'v1.33.0', updated: '2026-05-06' },
  { data: 'Residual value', source: 'Argonne ANL/ESD-22/2', version: 'v1.21.0', updated: '2022 (publication)' },
  { data: 'Idle fuel', source: 'Argonne / DOE Fact #861', version: 'v1.21.0', updated: '2012-2015 (publication)' },
  { data: 'Vocation derate', source: 'NREL Fleet DNA', version: 'v1.35.1', updated: '2014 (data collection)' },
  { data: 'Registration fees', source: 'State schedules', version: 'v1.21.0', updated: '2026-01' },
  { data: 'Fuel & electricity prices', source: 'EIA weekly API', version: 'live', updated: 'weekly' },
];

interface SectionProps {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
  citation?: string;
  confidence?: 'high' | 'medium' | 'low' | 'na';
}

function Section({ eyebrow, title, children, citation, confidence }: SectionProps) {
  return (
    <AccentCard stripe="ink" style={{ marginBottom: sectionGap }}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 style={sectionTitleStyle}>{title}</h2>
      <div style={bodyStyle}>{children}</div>
      {(citation || confidence) && (
        <div style={citationStyle}>
          {citation && <div style={{ marginBottom: confidence ? 6 : 0 }}>{citation}</div>}
          {confidence && (
            <span style={confidenceBadgeStyle(confidence)}>
              {confidence === 'na' ? 'N/A' : `${confidence} confidence`}
            </span>
          )}
        </div>
      )}
    </AccentCard>
  );
}

export default function MethodologyPage() {
  const user = useAuthStore((s) => s.user);
  const appMode = useScenarioStore((s) => s.app_mode);

  const showFleetDetail = !!user && appMode === 'fleet';

  useEffect(() => {
    document.body.classList.add('tco-ledger');
    return () => {
      document.body.classList.remove('tco-ledger');
    };
  }, []);

  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        <Eyebrow>Reference</Eyebrow>
        <h1 style={headingStyle}>Methodology &amp; Data Sources</h1>
        <p style={introStyle}>
          VehicleTCO computes total cost of ownership from government,
          manufacturer, and academic sources. Every number in the app
          traces back to one of those sources. The summary below explains
          each calculation, where the data comes from, and how confident
          we are in it.
        </p>

        <Section
          eyebrow="01"
          title="Residual Value"
          citation={
            showFleetDetail
              ? 'Argonne National Laboratory ANL/ESD-22/2 (Rev. 1, July 2022). Light-duty curves are extracted directly from PDF coefficients (Tables 4 and 6). Medium-duty (Class 3-7) curves extend the framework via the medium_duty_truck profile and are flagged argonne_synthesis.'
              : undefined
          }
          confidence={showFleetDetail ? 'high' : undefined}
        >
          We project the value a vehicle retains at the end of your
          ownership horizon using Argonne National Laboratory's residual
          value curves, parametrised by powertrain (gas, hybrid, plug-in
          hybrid, EV) and vehicle class. You can override the residual
          dollar amount in the Purchase tab if you have a better quote.
        </Section>

        <Section
          eyebrow="02"
          title="Fuel Economy"
          citation={
            showFleetDetail
              ? 'EPA fueleconomy.gov for 751 of 853 rows (test-cycle MPG / kWh-per-100mi). 59 rows fill EPA gaps with manufacturer-published specs, primarily new-model-year nameplates. Vocation-specific city / highway split applied per apps/api/data/vocation_config.py.'
              : undefined
          }
          confidence={showFleetDetail ? 'high' : undefined}
        >
          Passenger-vehicle fuel economy comes from EPA's
          fueleconomy.gov database. EVs use kWh per 100 miles in place
          of MPG. The annual fuel cost combines city and highway MPG
          using a vocation-specific weighting (a long-haul truck spends
          90% of its miles at highway MPG; a local-delivery truck
          spends 75% at city MPG).
        </Section>

        <Section
          eyebrow="03"
          title="Idle Fuel"
          citation={
            showFleetDetail
              ? 'Argonne "To Idle or Not To Idle" (2012) and DOE Vehicle Technologies Office Fact #861 (2015). Default daily idle hours come from the vocation profile (e.g. 0.5 hr/day local delivery, 8.0 hr/day long-haul sleeper); users override in the Operations tab.'
              : undefined
          }
          confidence={showFleetDetail ? 'medium' : undefined}
        >
          Vehicles burn fuel at idle even when they're not moving.
          We multiply Argonne's measured idle fuel rate (gallons per
          hour) by your daily idle hours to estimate annual idle cost.
        </Section>

        <Section
          eyebrow="04"
          title="Opportunity Cost"
          citation={
            showFleetDetail
              ? 'Formula: opportunity_cost = avg_committed_capital × rate × years, where avg_committed_capital = (cash_at_purchase + residual_at_sale) / 2. Default rate 7.0% (user-adjustable). Simple interest, not compound.'
              : undefined
          }
          confidence={showFleetDetail ? 'na' : undefined}
        >
          We calculate the investment income you could have earned on
          the money tied up in the vehicle, accounting for the fact
          that you recover some of that money when you sell. The
          default assumption is 7% annual return; you can adjust this
          in the scenario settings.
        </Section>

        <Section
          eyebrow="05"
          title="Year-Over-Year Cost Model"
          citation="Maintenance follows a power-law model: cost = base × (accumulated_miles / 15,000)^exponent × vocation_KI_modifier. Base costs/exponents per cohort sourced from NPTC 2022, ARI/Holman, AAA 2023, Fitch 1994, Vincentric, ATA. KI modifiers derived from NREL Fleet DNA medians per vocation_profile (sqrt-compressed; long_haul=1.0 baseline). Fuel inflation: EIA AEO. Insurance step-up: CIAB CLIPS."
          confidence="medium"
        >
          Maintenance cost is driven by accumulated mileage and duty-cycle
          intensity, not calendar age. Year N maintenance =
          {' '}<strong style={{ color: 'var(--tco-ink)' }}>base × (miles_N / 15,000)<sup>exp</sup> × KI_modifier</strong>,
          where the exponent is sub-linear (0.65 passenger → 0.80 heavy
          duty) so doubling miles less than doubles cost. Vocation KI
          modifiers range from 0.85 (passenger) to 2.34 (school buses),
          derived from NREL Fleet DNA by square-root compressing raw
          kinematic intensity ratios. Fuel cost compounds at a fuel-type
          default (diesel 2.5%/yr, gasoline 2.3%/yr, electricity 2.0%/yr);
          insurance compounds at 6.0%/yr (commercial) or 5.0%/yr (personal).
          All three escalators are user-overridable per scenario.
          {' '}<strong style={{ color: 'var(--tco-ink)' }}>local_delivery</strong>{' '}
          modifier is capped at refuse — Fleet DNA's 2014 vintage may
          overstate current local-delivery intensity given last-mile
          e-commerce growth.
        </Section>

        <Section eyebrow="06" title="Data Sources">
          <ul style={{ margin: '8px 0 0 18px', padding: 0, color: 'var(--tco-ink-2)' }}>
            <li style={{ marginBottom: 6 }}>
              <strong style={{ color: 'var(--tco-ink)' }}>EPA fueleconomy.gov</strong>
              {' '}— passenger fuel economy and EV efficiency
            </li>
            <li style={{ marginBottom: 6 }}>
              <strong style={{ color: 'var(--tco-ink)' }}>Argonne National Laboratory</strong>
              {' '}— residual value curves and idle fuel rates
            </li>
            <li style={{ marginBottom: 6 }}>
              <strong style={{ color: 'var(--tco-ink)' }}>NREL Fleet DNA</strong>
              {' '}— commercial vehicle real-world duty cycle data
            </li>
            <li style={{ marginBottom: 6 }}>
              <strong style={{ color: 'var(--tco-ink)' }}>Manufacturer fleet pages, KBB, Edmunds</strong>
              {' '}— MSRP and commercial vehicle specifications
            </li>
            <li>
              <strong style={{ color: 'var(--tco-ink)' }}>EIA weekly fuel price API</strong>
              {' '}— live gasoline, diesel, and electricity prices
            </li>
          </ul>
          {/* Phase 22.7: brief mention of the resolution chain in the
              individual / public view. The full explanation lives in the
              fleet-detail section below. */}
          <p style={{ ...bodyStyle, maxWidth: 720, marginTop: 12 }}>
            <strong style={{ color: 'var(--tco-ink)' }}>Fuel price resolution.</strong>
            {' '}For each vehicle, the calc engine uses your per-vehicle
            override if set, otherwise the scenario-wide fuel price you
            entered, and otherwise the EIA cached default. Per-vehicle
            wins, scenario-default fills the rest, EIA is the safety net.
          </p>
        </Section>

        {showFleetDetail && (
          <>
            <div style={{ margin: '32px 0 16px', borderTop: '1px solid var(--tco-line)', paddingTop: 24 }}>
              <Eyebrow>Fleet detail</Eyebrow>
              <h2 style={{ ...sectionTitleStyle, fontSize: 22, marginTop: 8 }}>
                Commercial vehicle calculations
              </h2>
              <p style={{ ...bodyStyle, maxWidth: 720 }}>
                The commercial-vehicle calc path layers a real-world
                derate on top of manufacturer-rated MPG and routes
                dealer-quote MSRPs through medium-duty residual and
                idle-fuel tables. The expansions below cover the
                math.
              </p>
            </div>

            <Section
              eyebrow="07"
              title="Commercial Fuel Economy & Vocation Derate"
              citation="NREL/NLR Fleet DNA composite (4,705 vehicle-days, 2014). Baseline cohort: long_haul Class 7 (KI=0.314, idle=0.183, 42.4 mph). Three penalty terms: KI excess (25%), idle excess (35%), speed deficit (10%). Recalibrated 2026-05-07 (v1.35.1) — the prior baseline collapsed 78 of 80 entries to the floor."
              confidence="medium"
            >
              Manufacturer-rated MPG describes test-track conditions.
              Real-world commercial fuel economy is degraded by
              kinematic intensity (acceleration cycles), idle time,
              and stop-and-go vs steady cruise. We apply a multiplier
              from 0.45 (refuse / compaction) to 0.98 (long-haul
              highway) using NREL's Fleet DNA dataset of 4,705
              real-world vehicle-days. Confidence is MEDIUM: the
              Fleet DNA data is from 2014 and engine technology has
              improved since then, so these factors are
              order-of-magnitude estimates pending recalibration.
            </Section>

            <AccentCard stripe={null} style={{ marginBottom: sectionGap }}>
              <Eyebrow>Vocation derate table</Eyebrow>
              <h2 style={sectionTitleStyle}>By vocation and GVWR class (diesel)</h2>
              <p style={{ ...bodyStyle, marginBottom: 16 }}>
                Multiplier applied to manufacturer-rated MPG. Gasoline
                values are diesel minus 0.05, clipped to the
                [0.45, 0.98] range.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Vocation</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Class 3</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Class 4</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Class 5</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Class 6</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Class 7</th>
                    </tr>
                  </thead>
                  <tbody>
                    {VOCATION_DERATE_TABLE.map((row) => (
                      <tr key={row.vocation}>
                        <td style={{ ...tdStyle, color: 'var(--tco-ink)' }}>{row.vocation}</td>
                        {row.values.map((v, idx) => (
                          <td key={idx} style={{ ...tdStyle, textAlign: 'right' }}>
                            {v == null ? '—' : v.toFixed(3)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </AccentCard>

            <Section
              eyebrow="08"
              title="Commercial MSRP Data Lineage"
              citation="Phase 21C / 21C.1 / 21D-A / 21D-B. Sources: apps/api/data/sources/phase_21c_chassis_cab_sources.json, phase_21c_1_chassis_cab_sources.json. Two-source verification rule: every database MSRP confirmed by at least two independent sources (manufacturer fleet pages, dealer Monroney, Edmunds, KBB)."
              confidence="medium"
            >
              Every database MSRP is confirmed by at least two
              independent sources before it ships. Dealer-locked
              nameplates (Isuzu, Hino, Freightliner, Ford Transit
              chassis-cab) require user entry of a dealer quote
              MSRP — the catalog provides the metadata routing so
              the calc engine still applies the correct residual
              and idle-fuel paths.
            </Section>

            <Section
              eyebrow="09"
              title="Fuel Price Resolution"
              citation="EIA weekly retail price survey (gasoline, diesel) and EIA Electric Power Monthly (electricity). Resolver implemented in apps/api/services/vocation_calc.resolve_fuel_price. Source attribution recorded per-vehicle in the calc engine metadata."
              confidence="high"
            >
              For each vehicle in a scenario, the calc engine resolves
              fuel price with a three-tier priority chain:
              <strong style={{ color: 'var(--tco-ink)' }}>
                {' '}per-vehicle override
              </strong>
              {' '}wins over the
              <strong style={{ color: 'var(--tco-ink)' }}>
                {' '}scenario-wide default
              </strong>
              {' '}wins over the
              <strong style={{ color: 'var(--tco-ink)' }}>
                {' '}EIA cached fallback
              </strong>
              . On a multi-fuel fleet (gasoline pickups + diesel vans
              + a few EVs), one scenario-level price per fuel type
              applies to every vehicle of that type unless individually
              overridden — useful for regional differences, fleet fuel
              cards, or alternative supply contracts.
            </Section>

            <Section
              eyebrow="10"
              title="Tax Benefits (Section 179 + Bonus Depreciation)"
              citation="IRC §179, IRC §168(k), IRS Rev. Proc. 2024-21 (luxury auto limits). Engine in apps/api/engine/tax_benefit.py. Federal-only scope; state conformity, MACRS years 2-7, AMT, and recapture are not modeled."
              confidence="medium"
            >
              First-year federal tax benefit on qualifying business
              vehicles. The credit is{' '}
              <strong style={{ color: 'var(--tco-ink)' }}>
                Section 179 expensing + bonus depreciation × marginal tax rate
              </strong>
              . Class 1–2 passenger vehicles are subject to the IRS
              luxury auto cap ($12,400 in 2024); Class 3+ commercial
              vehicles are not. Bonus depreciation is phasing out:
              60% in 2024, 40% in 2025, 20% in 2026, 0% in 2027+.
              Treated as a year-1 cash flow that reduces effective
              acquisition cost; the residual value calc and the
              depreciation curve remain physical-depreciation
              accounting.{' '}
              <strong style={{ color: 'var(--tco-accent)' }}>
                Consult a tax professional before making purchasing
                decisions based on tax projections.
              </strong>
            </Section>

            <AccentCard stripe={null} style={{ marginBottom: sectionGap }}>
              <Eyebrow>Data freshness</Eyebrow>
              <h2 style={sectionTitleStyle}>When each input was last updated</h2>
              <div style={{ overflowX: 'auto', marginTop: 12 }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Data</th>
                      <th style={thStyle}>Source</th>
                      <th style={thStyle}>Version</th>
                      <th style={thStyle}>Last updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FRESHNESS_ROWS.map((row) => (
                      <tr key={row.data}>
                        <td style={{ ...tdStyle, color: 'var(--tco-ink)' }}>{row.data}</td>
                        <td style={tdStyle}>{row.source}</td>
                        <td style={tdStyle}>{row.version}</td>
                        <td style={tdStyle}>{row.updated}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </AccentCard>

            <AccentCard stripe={null} style={{ marginBottom: sectionGap }}>
              <DisclosureRow label="Citing this tool">
                <p style={{ ...bodyStyle, fontStyle: 'italic', marginTop: 8 }}>
                  Vehicle total cost of ownership estimated using
                  VehicleTCO (tools.gormanholdings.com), incorporating
                  data from EPA fueleconomy.gov, Argonne National
                  Laboratory ANL/ESD-22/2, and NREL/NLR Fleet DNA
                  (2014).
                </p>
              </DisclosureRow>
            </AccentCard>
          </>
        )}

        <WorksCited />

        <div
          style={{
            marginTop: 40,
            paddingTop: 16,
            borderTop: '1px solid var(--tco-line)',
            fontSize: 11,
            color: 'var(--tco-ink-3)',
            letterSpacing: '0.04em',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <span>Last updated v1.44.1 · May 2026</span>
          <Link
            to="/"
            style={{
              color: 'var(--tco-ink-2)',
              textDecoration: 'none',
              borderBottom: '1px solid var(--tco-line)',
              paddingBottom: 1,
            }}
          >
            Back to scenarios →
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Works Cited ────────────────────────────────────────────────────────────

interface CitationGroup {
  heading: string;
  entries: React.ReactNode[];
}

const WORKS_CITED: CitationGroup[] = [
  {
    heading: 'Vehicle Depreciation',
    entries: [
      <>
        Argonne National Laboratory. (2022). <em>Costs, Effectiveness,
        and Deployment of Fuel Economy Technologies for Light-Duty
        Vehicles</em> (ANL/ESD-22/2). doi.org/10.2172/1891682
      </>,
      <>
        iSeeCars. (2026). <em>Vehicle Depreciation and Resale Value
        Analysis</em>. Retrieved March 2026. iseecars.com
      </>,
    ],
  },
  {
    heading: 'Fuel Economy (MPG)',
    entries: [
      <>
        U.S. Environmental Protection Agency. (2026). <em>Fuel Economy
        Guide</em>. Fueleconomy.gov API. (Live data, retrieved at
        time of calculation.)
      </>,
      <>
        Manufacturer specification sheets (commercial vehicles).
        Cited per-vehicle in MSRP table.
      </>,
    ],
  },
  {
    heading: 'Fuel Prices',
    entries: [
      <>
        U.S. Energy Information Administration. (2026). <em>Weekly
        Retail Gasoline and Diesel Prices</em>. EIA APIv2 PET series.
        api.eia.gov (Live data, retrieved at calculation time.)
      </>,
    ],
  },
  {
    heading: 'Fleet Vocation Fuel Derate',
    entries: [
      <>
        National Renewable Energy Laboratory. (2014). <em>Fleet DNA:
        Commercial Fleet Vehicle Operating Data</em>. NREL/NLR Fleet DNA
        composite dataset. 4,705 vehicle-days. Kinematic profiles by
        vocation and vehicle class. nrel.gov/transportation/fleetdna.html
        {' '}— 2014 vintage; recalibration recommended.
      </>,
    ],
  },
  {
    heading: 'Vehicle Maintenance Cost Model',
    entries: [
      <>
        National Private Truck Council. (2022). <em>NPTC Fleet
        Operating Cost Survey</em>. Arlington, VA: NPTC.
      </>,
      <>
        ARI Fleet / Holman Enterprises. (Annual). <em>Fleet Cost
        Benchmarking Report</em>.
      </>,
      <>
        AAA. (2023). <em>Your Driving Costs: How Much Are You Really
        Paying to Drive?</em> American Automobile Association.
      </>,
      <>
        Fitch, J.W. (1994). <em>Motor Fleet Management</em> (4th ed.).
        Englewood Cliffs, NJ: Prentice Hall. (Power-law cost elasticity
        model form.)
      </>,
      <>
        Vincentric LLC. (Annual). <em>Fleet Cost Analysis Methodology</em>.
        (Medium-duty power-law exponent reference.)
      </>,
      <>
        American Trucking Associations. <em>Cost Data</em>. (Heavy-duty
        exponent.)
      </>,
      <>
        National Renewable Energy Laboratory. (2014). <em>Fleet DNA
        Kinematic Summary</em>. Vocation KI values used as maintenance
        intensity modifiers (sqrt-compressed; long_haul = 1.0).
      </>,
    ],
  },
  {
    heading: 'Federal Tax Benefit',
    entries: [
      <>Internal Revenue Code §179 (26 U.S.C. § 179).</>,
      <>Internal Revenue Code §168(k) (26 U.S.C. § 168(k)).</>,
      <>IRS Revenue Procedure 2024-21 (luxury auto limits).</>,
      <>Tax Cuts and Jobs Act of 2017 (bonus depreciation schedule).</>,
    ],
  },
  {
    heading: 'Vehicle MSRP',
    entries: [
      <>
        Kelley Blue Book, Edmunds, and manufacturer suggested retail
        price lists. Data vintage: March 2026. Commercial-vehicle MSRPs
        sourced from dealer quotes where manufacturer pricing is not
        publicly listed.
      </>,
    ],
  },
  {
    heading: 'Fuel Inflation and Insurance Step-Up Defaults',
    entries: [
      <>
        U.S. Energy Information Administration. (2026). <em>Annual
        Energy Outlook 2026</em>. eia.gov/aeo
      </>,
      <>
        Council of Insurance Agents & Brokers. (Annual). <em>Commercial
        Lines Insurance Pricing Survey (CLIPS)</em>.
      </>,
    ],
  },
  {
    heading: 'Opportunity Cost',
    entries: [
      <>
        Opportunity cost computed as present value of capital tied up in
        the vehicle. Default investment return rate (5–7%) based on
        Vanguard and Fidelity long-term index fund return data.
      </>,
    ],
  },
];

function WorksCited() {
  return (
    <AccentCard stripe="ink" style={{ marginTop: 24, marginBottom: sectionGap }}>
      <Eyebrow>References</Eyebrow>
      <h2 style={sectionTitleStyle}>Data Sources &amp; Works Cited</h2>
      <p style={{ ...bodyStyle, marginBottom: 12 }}>
        Every number in the calculator traces back to a published source.
        Citations are grouped by the calculation they feed.
      </p>
      <p style={{ marginBottom: 16 }}>
        <Link
          to="/works-cited"
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--tco-accent)',
            textDecoration: 'none',
            borderBottom: '1px solid var(--tco-line)',
            paddingBottom: 1,
          }}
        >
          View full data sources &rarr;
        </Link>
      </p>
      {WORKS_CITED.map((group) => (
        <div key={group.heading} style={{ marginBottom: 16 }}>
          <h3
            style={{
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--tco-ink)',
              margin: '0 0 8px',
            }}
          >
            {group.heading}
          </h3>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--tco-ink-2)' }}>
            {group.entries.map((entry, idx) => (
              <li
                key={idx}
                style={{
                  fontSize: 13,
                  lineHeight: 1.55,
                  marginBottom: 6,
                }}
              >
                {entry}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </AccentCard>
  );
}
